/* =============================================================================
   CONFIGURAÇÃO DO SITE — editada no painel ADMIN, servida pelo BACKEND.

   Em produção estes valores vêm de:   GET /api/site-config
   (o admin edita no painel; o backend devolve o JSON no shape de `SiteConfig`).

   >>> O TIPO `SiteConfig` ABAIXO É O CONTRATO. <<<
   O backend deve responder um objeto com este mesmo formato. Pode omitir
   campos: o front faz merge sobre `defaultSiteConfig` (mergeSiteConfig()).

   Enquanto a rota /api/site-config não existir, o app usa `defaultSiteConfig`.
   PARA CONECTAR AO BACK: basta implementar `GET /api/site-config` devolvendo
   { ...SiteConfig }. O front já carrega no boot via getSiteConfig()
   (src/services/api.ts) e distribui para os componentes — nada mais a mudar.

   Cada campo tem um comentário com o RÓTULO que aparece pro admin.
   ============================================================================= */

/** Links de comunidade e corretora — todos editáveis no admin. */
export interface SiteLinks {
  instagram: string;   // admin › "Instagram" (URL do perfil) — vazio esconde o item
  telegram: string;    // admin › "Telegram" (URL do canal/grupo) — vazio esconde
  whatsapp: string;    // admin › "Comunidade (WhatsApp)" (URL do grupo) — vazio esconde
  youtube: string;     // admin › "YouTube" (URL do canal) — vazio esconde
  broker: string;      // admin › "Corretora — link de cadastro"
  brokerName: string;  // admin › "Corretora — título do card"
  brokerDesc: string;  // admin › "Corretora — descrição do card"
}

/** Popup genérico (boas-vindas e "liberar acesso"). */
export interface PopupConfig {
  enabled?: boolean;       // admin › liga/desliga o popup
  kicker: string;          // admin › rótulo pequeno acima do título
  title: string;           // admin › título
  text: string;            // admin › texto
  ctaLabel: string;        // admin › texto do botão principal
  ctaUrl: string;          // admin › link do botão principal
  secondaryLabel: string;  // admin › texto do botão de fechar ("agora não")
  image?: string;          // admin › imagem opcional (URL) — vazio = sem imagem
}

/** Faixa de "live agendada" na home (horário de Brasília). */
export interface LiveConfig {
  enabled: boolean;        // admin › liga/desliga a faixa de live
  title: string;           // admin › título da live
  subtitle: string;        // admin › subtítulo (quando ao vivo)
  url: string;             // admin › link da sala/live — vazio usa a URL do produto de live
  days: number[];          // admin › dias da semana (0=dom, 1=seg … 6=sáb)
  start: string;           // admin › início "HH:MM"
  end: string;             // admin › fim "HH:MM"
  offlineText: string;     // admin › texto exibido fora do horário
}

/** Aviso do sininho de notificações. */
export interface NotificationItem {
  id?: string;             // admin › id estável (recomendado p/ marcar como lida). Se ausente, front usa título+texto.
  title: string;           // admin › título do aviso
  text: string;            // admin › texto do aviso
  url?: string;            // admin › link opcional ao clicar
}

/** Contrato completo devolvido por GET /api/site-config. */
export interface SiteConfig {
  brand: { pre: string; suf: string };   // admin › nome da marca ("Blue" + "Capital") — vira "Blue.Capital"
  links: SiteLinks;
  welcomePopup: PopupConfig;              // popup de boas-vindas (aparece 1x por sessão ao entrar)
  unlockPopup: PopupConfig;               // popup do botão "Liberar todo o acesso"
  live: LiveConfig;
  notifications: NotificationItem[];      // avisos do sininho (admin gerencia a lista)
}

/* -----------------------------------------------------------------------------
   DEFAULTS (exemplos) — usados enquanto o backend não devolve /api/site-config.
   Troque pelos valores oficiais no painel admin, não aqui.
   -------------------------------------------------------------------------- */
export const defaultSiteConfig: SiteConfig = {
  brand: { pre: "Blue", suf: "Capital" },

  links: {
    instagram: "https://instagram.com/bluecapital.pro",
    telegram: "https://t.me/bluecapital",
    whatsapp: "https://chat.whatsapp.com/",
    youtube: "",
    broker: "https://entrar.bluecapital.pro/login",
    brokerName: "Acessar corretora",
    brokerDesc: "Cadastre-se na corretora parceira",
  },

  welcomePopup: {
    enabled: true,
    kicker: "Bem-vindo à Blue Capital",
    title: "Entre na nossa comunidade",
    text: "Participe do grupo do WhatsApp para acompanhar as lives, receber os sinais do dia e ficar por dentro de tudo em primeira mão.",
    ctaLabel: "Entrar no grupo",
    ctaUrl: "https://chat.whatsapp.com/",
    secondaryLabel: "Agora não",
    image: "",
  },

  unlockPopup: {
    kicker: "Acesso total",
    title: "Libere todo o acesso",
    text: "Desbloqueie todos os cursos, ferramentas e mentorias da Blue Capital em um único combo com o melhor preço.",
    ctaLabel: "Quero liberar tudo",
    ctaUrl: "",
    secondaryLabel: "Talvez depois",
    image: "",
  },

  live: {
    enabled: true,
    title: "Operações ao vivo",
    subtitle: "Day trading ao vivo agora no canal — entradas em tempo real.",
    url: "",
    days: [1, 2, 3, 4, 5],
    start: "20:00",
    end: "22:00",
    offlineText: "Enquanto isso, bora estudar e acompanhar as notícias do mercado.",
  },

  notifications: [
    {
      id: "live-hoje",
      title: "Live hoje às 20h",
      text: "Operações ao vivo no canal — não perca as entradas do dia.",
      url: "",
    },
  ],
};

/**
 * Aplica o que veio do backend por cima dos defaults (merge de 1 nível por seção).
 * Campos omitidos pelo backend mantêm o default — assim o admin pode configurar
 * só uma parte sem quebrar o resto.
 */
export function mergeSiteConfig(remote?: Partial<SiteConfig> | null): SiteConfig {
  if (!remote) return defaultSiteConfig;
  return {
    brand: { ...defaultSiteConfig.brand, ...(remote.brand || {}) },
    links: { ...defaultSiteConfig.links, ...(remote.links || {}) },
    welcomePopup: { ...defaultSiteConfig.welcomePopup, ...(remote.welcomePopup || {}) },
    unlockPopup: { ...defaultSiteConfig.unlockPopup, ...(remote.unlockPopup || {}) },
    live: { ...defaultSiteConfig.live, ...(remote.live || {}) },
    notifications: Array.isArray(remote.notifications)
      ? remote.notifications
      : defaultSiteConfig.notifications,
  };
}

/**
 * Config estático (fallback). O App substitui por getSiteConfig() no boot e
 * distribui via props. Mantido para retrocompatibilidade de imports.
 */
export const siteConfig: SiteConfig = defaultSiteConfig;
